import { Fragment } from 'react'
import styled from 'styled-components'

import { theme } from '@/styles/theme'
import { formatDate } from '@/utils/date'

import { ChartAnnotation } from '@/components/elements/ChartAnnotation'
import { Spacer } from '@/components/elements/Spacer'
import {
  Table,
  Thead,
  Tr,
  Th,
  Td,
  TbodyWithThreeColumnHeadings,
} from '@/components/elements/Table'
import { AHeading, BHeading } from '@/components/layouts/Ir/Heading'
import { Annotation, Paragraph } from '@/components/layouts/Ir/Paragraph'

import { useMediaQuery } from '@/hooks/useMediaQuery'
import { MonthlySales } from '@/types/monthlySales'

type Props = {
  monthlySales: MonthlySales
}

const dayNumberToEnglish = (dayNumber: string): string => {
  // dayNumberが1桁かつ10の位が0の場合
  if (dayNumber.length === 2 && dayNumber[0] === '0') {
    // 一の位の数字のみを取り出す
    return dayNumber[1]; // 先頭の0を除去して返す
  }
  return dayNumber;
};

export const MonthlySalesTable = ({ monthlySales }: Props) => {
  return (
    <>
      <AHeading>{monthlySales.titleJA}</AHeading>
      <Paragraph>毎月10日頃に更新予定です。</Paragraph>
      <Spacer h={{ sp: 10, pc: 20 }} />
      <PublicationDate>
        {formatDate(monthlySales.newpublicationDate,'yyyy年')} 
        {dayNumberToEnglish(formatDate(monthlySales.newpublicationDate,'MM月'))}
        {dayNumberToEnglish(formatDate(monthlySales.newpublicationDate,'dd日'))}
        <br />
        (前年同月比：％)
      </PublicationDate>
      <Spacer h={{ sp: 10, pc: 20 }} />
      <section>
        <Table>
          <>
            <Thead>
              <Tr>
                <Th rowSpan={2} colSpan={3} />
                <Th colSpan={9} color={'secondary'}>
                  {monthlySales.fHyear}
                </Th>
                <Th colSpan={9} color={'secondary'}>
                  {monthlySales.sHyear}
                </Th>
                <Th rowSpan={2} color={'secondary'}>
                  通期
                </Th>
              </Tr>
              <Tr>
                <Th color="quaternary">7月</Th>
                <Th color="quaternary">8月</Th>
                <Th color="quaternary">9月</Th>
                <QuarterTh color="tertiary">1Q</QuarterTh>
                <Th color="quaternary">10月</Th>
                <Th color="quaternary">11月</Th>
                <Th color="quaternary">12月</Th>
                <QuarterTh color="tertiary">2Q</QuarterTh>
                <Th color="secondary">上期</Th>
                <Th color="quaternary">1月</Th>
                <Th color="quaternary">2月</Th>
                <Th color="quaternary">3月</Th>
                <QuarterTh color="tertiary">3Q</QuarterTh>
                <Th color="quaternary">4月</Th>
                <Th color="quaternary">5月</Th>
                <Th color="quaternary">6月</Th>
                <QuarterTh color="tertiary">4Q</QuarterTh>
                <Th color="secondary">下期</Th>
              </Tr>
            </Thead>
            <TbodyWithThreeColumnHeadings firstHeadWidth="35px" secondHeadWidth='35px'>
              <Tr>
                <Th rowSpan={7} noBorder style={{ whiteSpace: 'nowrap' }}>
                  ト<br />ラ<br />イ<br />ア<br />ル
                </Th>
                <Th rowSpan={3} style={{ whiteSpace: 'nowrap' }}>
                  既<br />存<br />店
                </Th>
                <SmallTh>売上高</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.oShopSales ?? ' '}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.oShopSales ?? ' '}
                  </SmallTd>
                ))}
                <SmallTd>{monthlySales.fullYear.oShopSales ?? ' '}</SmallTd>
              </Tr>
              <Tr>
                <SmallTh>客数</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.oShopCustomers ?? ' '}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.oShopCustomers ?? ' '}
                  </SmallTd>
                ))}
                <SmallTd>{monthlySales.fullYear.oShopCustomers ?? ' '}</SmallTd>
              </Tr>
              <Tr>
                <SmallTh>客単価</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.oShopCustomerUnitPrice ?? ' '}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.oShopCustomerUnitPrice ?? ' '}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.oShopCustomerUnitPrice ?? ' '}
                </SmallTd>
              </Tr>
              <Tr>
                <Th rowSpan={4} style={{ whiteSpace: 'nowrap' }}>
                  全<br />店
                </Th>
                <SmallTh>売上高</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.aShopSales ?? ' '}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.aShopSales ?? ' '}
                  </SmallTd>
                ))}
                <SmallTd>{monthlySales.fullYear.aShopSales ?? ' '}</SmallTd>
              </Tr>
              <Tr>
                <SmallTh>新規出店数</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.newOpenShops}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.newOpenShops}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.allShops === 0
                    ? ' '
                    : monthlySales.fullYear.newOpenShops}
                </SmallTd>
              </Tr>
              {/* <Tr>
                <SmallTh>業態転換 (増加)*</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.toTrialSeiyu}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.toTrialSeiyu}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.allShops === 0
                    ? ' '
                    : monthlySales.fullYear.toTrialSeiyu}
                </SmallTd>
              </Tr> */}
              <Tr>
                <SmallTh>閉店店舗数</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.closeShops}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.closeShops}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.allShops === 0
                    ? ' '
                    : monthlySales.fullYear.closeShops}
                </SmallTd>
              </Tr>
              <Tr>
                <SmallTh>全店舗数</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.allShops}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.allShops === 0 ? ' ' : Sales.allShops}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.allShops === 0
                    ? ' '
                    : monthlySales.fullYear.allShops}
                </SmallTd>
              </Tr>
              <Tr>
                <Th rowSpan={9} noBorder style={{ whiteSpace: 'nowrap' }}>
                  西<br />友
                </Th>
                <Th rowSpan={3} style={{ whiteSpace: 'nowrap' }}>
                  既<br />存<br />店
                </Th>
                <SmallTh>売上高</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuOShopSales ?? ' '}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuOShopSales ?? ' '}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.seiyuOShopSales ?? ' '}
                </SmallTd>
              </Tr>
              <Tr>
                <SmallTh>客数</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuOShopCustomers ?? ' '}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuOShopCustomers ?? ' '}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.seiyuOShopCustomers ?? ' '}
                </SmallTd>
              </Tr>
              <Tr>
                <SmallTh>客単価</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuOShopCustomerUnitPrice ?? ' '}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuOShopCustomerUnitPrice ?? ' '}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.seiyuOShopCustomerUnitPrice ?? ' '}
                </SmallTd>
              </Tr>
              <Tr>
                <Th rowSpan={6} style={{ whiteSpace: 'nowrap' }}>
                  全<br />店
                </Th>
                <SmallTh>売上高</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAShopSales ?? ' '}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAShopSales ?? ' '}
                  </SmallTd>
                ))}
                <SmallTd>{monthlySales.fullYear.seiyuAShopSales ?? ' '}</SmallTd>
              </Tr>
              <Tr>
                <SmallTh>新規出店数</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuNewOpenShops}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuNewOpenShops}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.seiyuAllShops === 0
                    ? ' '
                    : monthlySales.fullYear.seiyuNewOpenShops}
                </SmallTd>
              </Tr>
              <Tr>
                <SmallTh>閉店店舗数</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuCloseShops}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuCloseShops}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.seiyuAllShops === 0
                    ? ' '
                    : monthlySales.fullYear.seiyuCloseShops}
                </SmallTd>
              </Tr>
              <Tr>
                <SmallTh>全店舗数</SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuAllShops}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuAllShops}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.seiyuAllShops === 0
                    ? ' '
                    : monthlySales.fullYear.seiyuAllShops}
                </SmallTd>
              </Tr>
              <Tr>
                <SmallTh>
                  業態転換数
                  <br />
                  (西友⇒{useMediaQuery('(max-width: 1047px)') ? <br /> : ''}
                  トライアル西友)
                </SmallTh>
                {monthlySales.fH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuToTrial}
                  </SmallTd>
                ))}
                {monthlySales.sH.map((Sales, index) => (
                  <SmallTd key={`${Sales}${index}`}>
                    {Sales.seiyuAllShops === 0 ? ' ' : Sales.seiyuToTrial}
                  </SmallTd>
                ))}
                <SmallTd>
                  {monthlySales.fullYear.seiyuAllShops === 0
                    ? ' '
                    : monthlySales.fullYear.seiyuToTrial}
                </SmallTd>
              </Tr>
            </TbodyWithThreeColumnHeadings>
          </>
        </Table>
      </section>
      <Spacer h={{ sp: 15, pc: 20 }} />
      <Paragraph>
        * 売上高の前年同月比はPOSデータに基づいた速報値であり、決算数値と異なる可能性があります。
      </Paragraph>
      <Paragraph>
        *「グロサリー」は菓子類などの加工食品、「デイリー」は卵や乳製品などの日配品、「フレッシュ」は生鮮四品(青果･精肉･鮮魚･惣菜)、「生活」は日用消耗品などの家庭用品、「ハード」は家電製品などの耐久性商品、「アパレル」は衣料品を示しております。
      </Paragraph>
      <Paragraph>
        *「トライアル西友」への業態転換店は、西友の店舗として集計しており、西友の合計店舗数に増減はありません。
      </Paragraph>
      <Spacer h={{ sp: 30, pc: 80 }} />
      <section>
        <SubHeading>概況コメント</SubHeading>
        <Spacer h={{ sp: 20, pc: 36 }} />
        <Paragraph>
          {monthlySales.summary.split('→').map((value, index) => (
            <Fragment key={`${index}${value}`}>
              {value}
              <br />
            </Fragment>
          ))}
        </Paragraph>
        <Spacer h={{ sp: 15, pc: 30 }} />
        <Annotation>
          {monthlySales.comments.split('→').map((value, index) => (
            <Fragment key={`${index}${value}`}>
              {value}
              <br />
            </Fragment>
          ))}
        </Annotation>
      </section>
    </>
  )
}

const SubHeading = styled(BHeading)`
  margin-top: 0;
  margin-bottom: 0;
`

const PublicationDate = styled(ChartAnnotation)`
  text-align: right;
`

const SmallTd = styled(Td)`
  padding: 11px 5px 14px 5px;

  &:nth-of-type(4),
  &:nth-of-type(8),
  &:nth-of-type(13),
  &:nth-of-type(17) {
    min-width: 48px;
    background: ${theme.colors.th.tertiary};
  }

  &:nth-of-type(9),
  &:nth-of-type(18),
  &:nth-of-type(19) {
    background: ${theme.colors.th.secondary};
  }
`

const SmallTh = styled(Th)`
  width: 85px;
  padding: 11px 9px 14px 9px;
  white-space: nowrap;
`

const QuarterTh = styled(Th)`
  min-width: 48px;
`
